import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog, TextField } from '@mui/material';
import { X } from 'lucide-react';

import Text from 'components/text';
import api from 'utils/api';
import { useCollabClubArtists } from 'hooks/swrRequests/useCollabClubArtists';
import { CcMatchPreview } from 'hooks/swrRequests/useAdminCcRequests';
import type { Artist } from 'types';
import { FONT } from './constants';
import FieldLabel from './components/field-label';
import ArtistPicker from './components/artist-picker';
import ArtistMultiPicker from './components/artist-multi-picker';
import ArtistProfileCard, { ArtistProfileCardHandle } from './components/artist-profile-card';
import PrimaryButton from './components/primary-button';
import SecondaryButton from 'components/admin/secondary-button';
import ConfirmLogRequestModal from './confirm-log-request-modal';

export interface LogRequestInitialValue {
  fromArtist: Artist;
  wants: Artist[];
  date?: string;
}

interface LogRequestModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  // When set (e.g. opened from a per-pair Connect), the form opens prefilled with this pair.
  initialValue?: LogRequestInitialValue | null;
}

const todayISO = (): string => new Date().toISOString().slice(0, 10);

const LogRequestModal: React.FC<LogRequestModalProps> = ({
  open,
  onClose,
  onCreated,
  initialValue,
}) => {
  const { artists, refetch: refetchArtists } = useCollabClubArtists();

  const [fromArtist, setFromArtist] = useState<Artist | null>(null);
  const [wants, setWants] = useState<Artist[]>([]);
  const [date, setDate] = useState<string>(todayISO());
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Confirmation step: previews whether submitting will create a match before committing.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [preview, setPreview] = useState<CcMatchPreview[] | null>(null);

  // Auto-save the requesting artist's profile edits as part of logging the request. The card
  // prefills itself from the selected artist, so no onboarding state is held here.
  const profileCardRef = useRef<ArtistProfileCardHandle>(null);

  // Reset (or prefill) the form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setErrorMsg(null);
    setConfirmOpen(false);
    setReviewing(false);
    setPreview(null);
    if (initialValue) {
      setFromArtist(initialValue.fromArtist);
      setWants(initialValue.wants);
      setDate(initialValue.date || todayISO());
    } else {
      setFromArtist(null);
      setWants([]);
      setDate(todayISO());
    }
  }, [open, initialValue]);

  // Requesting-artist options — include a prefilled artist that isn't in the roster list
  // so the Autocomplete value resolves cleanly.
  const artistOptions = useMemo(
    () =>
      fromArtist && !artists.some((a) => a.id === fromArtist.id)
        ? [fromArtist, ...artists]
        : artists,
    [artists, fromArtist]
  );

  // Targets exclude the requesting artist; include any prefilled want missing from the roster.
  const targetOptions = useMemo(() => {
    const base = artists.filter((a) => a.id !== fromArtist?.id);
    const missing = wants.filter((w) => !base.some((a) => a.id === w.id));
    return missing.length ? [...missing, ...base] : base;
  }, [artists, fromArtist, wants]);

  const handlePickArtist = (artist: Artist | null) => {
    setFromArtist(artist);
    setWants((prev) => prev.filter((a) => a.id !== artist?.id));
  };

  // After a profile save, refetch the roster and refresh the selected artist so its
  // ArtistIdentity (name / meta line) reflects the new details.
  const handleProfileSaved = async () => {
    const fresh = await refetchArtists();
    if (!fresh) return;
    setFromArtist((cur) => (cur ? fresh.find((a) => a.id === cur.id) ?? cur : cur));
  };

  const canSubmit = !!fromArtist && wants.length > 0 && !submitting && !reviewing;

  // Step 1 — preview whether the request will create a match, then open the confirm dialog.
  const handleReview = async () => {
    if (!fromArtist || wants.length === 0) return;
    setReviewing(true);
    setErrorMsg(null);
    try {
      const result = await api.collabClub.previewAdminRequest({
        data: { fromCcArtistId: fromArtist.id, ccArtistIds: wants.map((a) => a.id) },
      });
      // No-match comes back as null or an empty body ("") — normalize to null.
      setPreview(Array.isArray(result) ? result : null);
      setConfirmOpen(true);
    } catch (err: any) {
      setErrorMsg(
        err?.data?.message || err?.message || 'Failed to check for a match. Please try again.'
      );
    } finally {
      setReviewing(false);
    }
  };

  const handleSubmit = async () => {
    if (!fromArtist || wants.length === 0) return;
    setSubmitting(true);
    setErrorMsg(null);

    try {
      // Onboarding answers describe the artist, not the request — auto-save the profile card's
      // edits (no-op when nothing was filled in); a failure aborts before logging the request.
      await profileCardRef.current?.save();

      await api.collabClub.createAdminRequest({
        data: {
          fromCcArtistId: fromArtist.id,
          ccArtistIds: wants.map((a) => a.id),
          requestDate: date || null,
        },
      });

      setConfirmOpen(false);
      onCreated();
    } catch (err: any) {
      setErrorMsg(
        err?.data?.message || err?.message || 'Failed to log the request. Please try again.'
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            // Match the rest of the admin font (Founders Grotesk) on MUI inputs / avatars.
            '& .MuiInputBase-input': { fontFamily: 'inherit' },
            '& .MuiAvatar-root': { fontFamily: 'inherit' },
          },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          padding="22px 24px 18px"
        >
          <Text fontSize={FONT.lg} fontWeight={700}>
            Log a request manually
          </Text>
          <Box
            component="button"
            onClick={onClose}
            aria-label="Close"
            display="flex"
            sx={{
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: 0,
              color: '#64748b',
            }}
          >
            <X size={20} />
          </Box>
        </Box>

        {/* Body */}
        <Box
          padding="0 24px 8px"
          borderTop="1px solid #e2e8f0"
          paddingTop="20px"
          display="flex"
          flexDirection="column"
          gap={2.5}
          sx={{ maxHeight: '64vh', overflowY: 'auto' }}
        >
          {/* Requesting artist */}
          <Box>
            <FieldLabel required>Artist</FieldLabel>
            <ArtistPicker options={artistOptions} value={fromArtist} onChange={handlePickArtist} />
          </Box>

          {/* Wants to collab with — full width */}
          <Box>
            <FieldLabel required>Wants to collab with</FieldLabel>
            <ArtistMultiPicker options={targetOptions} value={wants} onChange={setWants} />
          </Box>

          {/* Profile of the requesting artist — editable, saved manually or on submit. */}
          {fromArtist && (
            <ArtistProfileCard
              key={fromArtist.id}
              ref={profileCardRef}
              artist={fromArtist}
              onSaved={handleProfileSaved}
            />
          )}

          {/* Date received — separate line at the end */}
          <Box>
            <FieldLabel>Date received</FieldLabel>
            <TextField
              type="date"
              size="small"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              sx={{ maxWidth: 240 }}
            />
          </Box>

          {errorMsg && (
            <Text fontSize={FONT.sm} color="#dc2626">
              {errorMsg}
            </Text>
          )}
        </Box>

        {/* Footer */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={2}
          padding="16px 24px"
          borderTop="1px solid #e2e8f0"
          marginTop="12px"
        >
          <Box display="flex" gap={1}>
            <SecondaryButton variant="text" onClick={onClose}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              variant="contained"
              onClick={handleReview}
              disabled={!canSubmit}
              startIcon={reviewing ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Review
            </PrimaryButton>
          </Box>
        </Box>
      </Dialog>

      <ConfirmLogRequestModal
        open={confirmOpen}
        preview={preview}
        fromArtist={fromArtist}
        wants={wants}
        submitting={submitting}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSubmit}
      />
    </>
  );
};

export default LogRequestModal;
